import {
  formatMealTypeLabel,
  joinMealItemNames,
} from "./resolveMealDisplayName";

/** Keys duplicated on save payload root — drop from nested food_analyze_data. */
const ANALYZE_ROOT_DUPLICATE_KEYS = [
  "document_id",
  "date",
  "meal_type",
  "meal_title",
  "food_items",
  "totals",
  "ghi_score",
  "ghi_label",
  "ghi_reasons",
  "other_details",
] as const;

export function readMealTitleFromAnalyzePayload(
  parsed: Record<string, unknown> | null | undefined,
): string | null {
  if (!parsed) return null;
  const data = parsed.data as Record<string, unknown> | undefined;
  const nested = data?.data as Record<string, unknown> | undefined;
  const result = parsed.result as Record<string, unknown> | undefined;
  for (const c of [
    parsed.meal_title,
    data?.meal_title,
    nested?.meal_title,
    result?.meal_title,
  ]) {
    if (typeof c === "string" && c.trim()) return c.trim();
  }
  return null;
}

export function resolveMealTitleForSave(options: {
  mealTitle?: string | null;
  analyzeData?: Record<string, unknown>;
  mealType?: string;
  foodItems?: Array<{ name?: string }>;
}): string | undefined {
  const fromState = options.mealTitle?.trim();
  if (fromState) return fromState;

  const fromAnalyze = readMealTitleFromAnalyzePayload(options.analyzeData);
  if (fromAnalyze) return fromAnalyze;

  const fromItems = joinMealItemNames(options.foodItems);
  if (fromItems) return fromItems;

  const typeLabel = formatMealTypeLabel(options.mealType);
  return typeLabel || undefined;
}

const stripRootDuplicates = (raw: Record<string, unknown>): Record<string, unknown> => {
  const next = { ...raw };
  for (const key of ANALYZE_ROOT_DUPLICATE_KEYS) {
    delete next[key];
  }
  return next;
};

const patchGiGlInAnalyzeData = (
  raw: Record<string, unknown>,
  giUsed: number | null,
  glUsed: number | null,
): Record<string, unknown> => {
  if (giUsed == null && glUsed == null) return raw;

  const patchSpike = (spike: unknown): unknown => {
    if (!spike || typeof spike !== "object" || Array.isArray(spike)) return spike;
    return {
      ...(spike as Record<string, unknown>),
      ...(giUsed != null ? { gi_used: giUsed } : {}),
      ...(glUsed != null ? { gl_used: glUsed } : {}),
    };
  };

  const patchTotals = (totals: unknown): unknown => {
    if (!totals || typeof totals !== "object" || Array.isArray(totals)) return totals;
    return {
      ...(totals as Record<string, unknown>),
      ...(giUsed != null ? { GI_estimate: giUsed } : {}),
      ...(glUsed != null ? { GL_estimate: glUsed } : {}),
    };
  };

  const next = { ...raw };
  if (next.glucose_spike) {
    next.glucose_spike = patchSpike(next.glucose_spike);
  }
  if (next.totals) {
    next.totals = patchTotals(next.totals);
  }

  const nested = next.data;
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    const nestedObj = { ...(nested as Record<string, unknown>) };
    if (nestedObj.glucose_spike) {
      nestedObj.glucose_spike = patchSpike(nestedObj.glucose_spike);
    }
    if (nestedObj.totals) {
      nestedObj.totals = patchTotals(nestedObj.totals);
    }
    next.data = nestedObj;
  }

  return next;
};

export type FamilyGroupSaveEntry = {
  group_id: string;
  is_selected: boolean;
  log_for_user_ids: string[];
};

const uniqueIds = (ids: string[]): string[] => {
  const seen = new Set<string>();
  return ids.filter(id => {
    const trimmed = id.trim();
    if (!trimmed || seen.has(trimmed)) return false;
    seen.add(trimmed);
    return true;
  });
};

/**
 * Self save → [logger id]
 * Family save → [logger id, ...selected member ids]
 */
export function buildLogForUserIds(
  currentUserId: string,
  selectedMemberIds: Set<string>,
): string[] | undefined {
  const loggerId = currentUserId.trim();
  if (!selectedMemberIds.size) {
    return loggerId ? [loggerId] : undefined;
  }

  const selected = Array.from(selectedMemberIds);
  return uniqueIds(loggerId ? [loggerId, ...selected] : selected);
}

/** Map UI multi-select to per-group log_for_user_ids (logger + selected in each group). */
export function buildSelectedFamilyGroups(
  familyGroups: Array<{ id: string; members?: { user_id: string }[] }>,
  selectedMemberIds: Set<string>,
  loggerUserId?: string,
): FamilyGroupSaveEntry[] {
  const loggerId = loggerUserId?.trim();

  return familyGroups
    .map(g => {
      const selectedInGroup = (g.members ?? [])
        .filter(m => selectedMemberIds.has(m.user_id))
        .map(m => m.user_id);
      if (!selectedInGroup.length) return null;

      const log_for_user_ids = uniqueIds(
        loggerId ? [loggerId, ...selectedInGroup] : selectedInGroup,
      );

      return { group_id: g.id, is_selected: true, log_for_user_ids };
    })
    .filter((entry): entry is FamilyGroupSaveEntry => entry != null);
}

/**
 * Analyze API often returns family_groups[].log_for_user_ids with every member.
 * Save payload must only reflect explicit UI intent:
 * - self: logger id only, require_approval false
 * - family: logger id + selected member ids, require_approval true
 */
export function buildFoodAnalyzeDataForSave(
  raw: Record<string, unknown> | undefined,
  options: {
    logForUserIds?: string[];
    requireApproval: boolean;
    familyGroupId?: string;
    familyGroups?: FamilyGroupSaveEntry[];
    giUsed?: number | null;
    glUsed?: number | null;
  },
): Record<string, unknown> | undefined {
  const { logForUserIds, requireApproval, familyGroupId, familyGroups, giUsed, glUsed } =
    options;
  const hasTargetIds = Boolean(logForUserIds?.length);

  if (!raw && !hasTargetIds) {
    return undefined;
  }

  const stripped = raw ? stripRootDuplicates(raw) : {};
  const patched = patchGiGlInAnalyzeData(stripped, giUsed ?? null, glUsed ?? null);

  delete patched.log_for_user_ids;
  delete patched.require_approval;
  delete patched.family_group_id;
  delete patched.pending_approvals;
  delete patched.family_groups;

  if (!requireApproval) {
    return {
      require_approval: false,
      ...(logForUserIds?.length ? { log_for_user_ids: logForUserIds } : {}),
      ...patched,
    };
  }

  const nestedFamilyGroups =
    familyGroups?.length
      ? familyGroups
      : familyGroupId
        ? [
            {
              group_id: familyGroupId,
              is_selected: true,
              log_for_user_ids: logForUserIds ?? [],
            },
          ]
        : undefined;

  return {
    log_for_user_ids: logForUserIds,
    require_approval: true,
    ...(familyGroupId ? { family_group_id: familyGroupId } : {}),
    ...(nestedFamilyGroups?.length ? { family_groups: nestedFamilyGroups } : {}),
    ...patched,
  };
}

/** Family / approval fields first for Network tab readability. */
export function orderSaveMealPayload<T extends Record<string, unknown>>(payload: T): T {
  if (!("document_id" in payload)) {
    return payload;
  }

  const {
    log_for_user_ids,
    require_approval,
    family_group_id,
    document_id,
    date,
    meal_type,
    meal_title,
    ghi_score,
    ghi_label,
    ghi_reasons,
    other_details,
    food_analyze_data,
    food_items,
    totals,
    ...rest
  } = payload;

  return {
    ...(Array.isArray(log_for_user_ids) && log_for_user_ids.length
      ? { log_for_user_ids }
      : {}),
    require_approval: require_approval ?? false,
    ...(family_group_id ? { family_group_id } : {}),
    document_id,
    date,
    meal_type,
    ...(meal_title ? { meal_title } : {}),
    ...(ghi_score != null ? { ghi_score } : {}),
    ...(ghi_label ? { ghi_label } : {}),
    ...(Array.isArray(ghi_reasons) && ghi_reasons.length ? { ghi_reasons } : {}),
    ...(other_details ? { other_details } : {}),
    ...(food_analyze_data ? { food_analyze_data } : {}),
    food_items,
    totals,
    ...rest,
  } as T;
}
